import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ApiRequestLog from '../components/ApiRequestLog';
import { useAuth } from '../context/AuthContext';
import {
  blocosDeEscopos, escoposValidos, exemploDeUso, exemploDeNome, nomeDaVertical,
} from '../data/escoposDaIntegracao';
import './ApiIntegration.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Onda 107: a lista de escopos saiu daqui. Eram oito, oferecidos iguais em
// qualquer vertical, e cada um abre rotas de uma vertical só — um escopo da
// vertical errada não falha, abre rotas que devolvem vazio. Quais valem onde, e
// o texto de cada um (a regra da Onda 65: a descrição diz o que o escopo
// REALMENTE abre), estão em `data/escoposDaIntegracao.js`.

// `CodeBlock` saiu na Onda 65, junto com a aba de documentação que o usava.

// ─── Aba 1: Gestão de Chaves ───────────────────────────────────────────────
function KeysTab({ token, vertical }) {
  const blocos = blocosDeEscopos(vertical);
  const validos = escoposValidos(vertical);
  // Estacionamento (ou vertical ainda não carregada): nenhuma rota que uma
  // chave abre serve aqui. A tela diz isso em vez de oferecer escopos sem uso.
  const semIntegracao = blocos.length === 0;
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', scopes: [], rate_limit_per_minute: 60 });
  const [formError, setFormError] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [copied, setCopied] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKeys(data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleScopeToggle = (scope) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.includes(scope) ? f.scopes.filter(s => s !== scope) : [...f.scopes, scope]
    }));
  };

  const handleCreate = async () => {
    setFormError('');
    if (!form.name.trim()) { setFormError('Informe um nome para a integração.'); return; }
    if (!form.scopes.length) { setFormError('Selecione ao menos um escopo de permissão.'); return; }
    try {
      const { data } = await axios.post(`${API_BASE}/api/api-keys`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewKey(data.api_key);
      setShowModal(false);
      setForm({ name: '', description: '', scopes: [], rate_limit_per_minute: 60 });
      loadKeys();
    } catch (e) {
      setFormError(e.response?.data?.error || 'Erro ao criar API Key.');
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm('Revogar esta API Key? Nenhuma requisição será aceita com ela.')) return;
    await axios.patch(`${API_BASE}/api/api-keys/${id}/revoke`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setActionMsg('API Key revogada com sucesso.');
    loadKeys();
    setTimeout(() => setActionMsg(''), 3000);
  };

  const handleActivate = async (id) => {
    await axios.patch(`${API_BASE}/api/api-keys/${id}/activate`, {}, { headers: { Authorization: `Bearer ${token}` } });
    setActionMsg('API Key reativada com sucesso.');
    loadKeys();
    setTimeout(() => setActionMsg(''), 3000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir permanentemente esta API Key? Esta ação não pode ser desfeita.')) return;
    await axios.delete(`${API_BASE}/api/api-keys/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    setActionMsg('API Key excluída permanentemente.');
    loadKeys();
    setTimeout(() => setActionMsg(''), 3000);
  };

  const handleCopy = () => {
    if (newKey?.key) {
      navigator.clipboard.writeText(newKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div>
          <h2>Chaves de API</h2>
          <p className="tab-desc">
            Gerencie as chaves de acesso para integração com sistemas externos. Cada chave possui escopos de
            permissão granulares.{' '}
            {!semIntegracao && <>Esta instalação é da vertical <strong>{nomeDaVertical(vertical)}</strong>, e só os
            escopos dela são oferecidos.</>}
          </p>
        </div>
        {!semIntegracao && (
          <button className="btn-primary" onClick={() => setShowModal(true)}>+ Nova API Key</button>
        )}
      </div>

      {semIntegracao && (
        <div className="alert alert-info api-sem-integracao">
          {vertical
            ? <>A integração por chave de API é das verticais <strong>Clube Social</strong> e <strong>Condomínio</strong>.
               Esta instalação é da vertical <strong>{nomeDaVertical(vertical)}</strong>, e nenhuma das rotas que uma
               chave abre serve a ela — por isso não há escopo a oferecer. As chaves que já existirem continuam
               listadas abaixo, e podem ser revogadas ou excluídas.</>
            : 'Carregando a vertical desta instalação…'}
        </div>
      )}

      {actionMsg && <div className="alert alert-success">{actionMsg}</div>}

      {/* Modal de criação */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nova API Key</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {formError && <div className="alert alert-error">{formError}</div>}
              <div className="form-group">
                <label>Nome da Integração *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder={exemploDeNome(vertical)}
                />
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Descreva o propósito desta integração..."
                  rows={2}
                />
              </div>
              <div className="form-group">
                <label>Escopos de Permissão *</label>
                {/* Onda 107: em blocos — o cadastro próprio da vertical e os
                    visitantes, que valem nas duas. Cada bloco diz a quem serve,
                    e escopo de outra vertical não aparece: aqui ele não teria
                    uso nenhum. */}
                {blocos.map(b => (
                  <fieldset key={b.id} className="scope-grupo">
                    <legend>{b.titulo}</legend>
                    <p className="scope-grupo-nota">{b.nota}</p>
                    <div className="scopes-grid">
                      {b.escopos.map(s => (
                        <label key={s.chave} className={`scope-item ${form.scopes.includes(s.chave) ? 'selected' : ''}`}>
                          <input
                            type="checkbox"
                            checked={form.scopes.includes(s.chave)}
                            onChange={() => handleScopeToggle(s.chave)}
                          />
                          <div>
                            <strong>{s.rotulo}</strong>
                            <small>{s.desc}</small>
                            <small className="scope-chave"><code>{s.chave}</code></small>
                            {/* Onda 65: o que se concede sem perceber fica em
                                destaque. Um escopo cuja descrição é menor que a
                                permissão faz o administrador entregar mais poder do
                                que pretende. */}
                            {s.aviso && <small className="scope-warning">⚠ {s.aviso}</small>}
                          </div>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>
              <div className="form-group">
                <label>Limite de Requisições por Minuto</label>
                <input
                  type="number"
                  value={form.rate_limit_per_minute}
                  onChange={e => setForm(f => ({ ...f, rate_limit_per_minute: parseInt(e.target.value) || 60 }))}
                  min={1} max={1000}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleCreate}>Gerar API Key</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de exibição da chave recém-criada */}
      {newKey && (
        <div className="modal-overlay">
          <div className="modal modal-key-reveal">
            <div className="modal-header key-reveal-header">
              <span className="key-reveal-icon">🔑</span>
              <h3>API Key Gerada com Sucesso</h3>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning">
                <strong>⚠️ Atenção:</strong> Esta é a única vez que a chave completa será exibida. Copie e armazene-a em local seguro agora.
              </div>
              <div className="key-display">
                <code>{newKey.key}</code>
                <button className="btn-copy" onClick={handleCopy}>{copied ? '✓ Copiado!' : 'Copiar'}</button>
              </div>
              <div className="key-meta">
                <span><strong>Nome:</strong> {newKey.name}</span>
                <span><strong>Escopos:</strong> {newKey.scopes?.join(', ')}</span>
                <span><strong>Limite:</strong> {newKey.rate_limit_per_minute} req/min</span>
              </div>
              <div className="key-usage-hint">
                <p><strong>Como usar:</strong></p>
                <code>{exemploDeUso(vertical, newKey.key, API_BASE)}</code>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-primary" onClick={() => setNewKey(null)}>Entendi, já copiei a chave</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Carregando chaves...</div>
      ) : keys.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">🔑</span>
          <p>Nenhuma API Key cadastrada. Crie a primeira para começar a integrar sistemas externos.</p>
        </div>
      ) : (
        <div className="keys-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Chave (prévia)</th>
                <th>Escopos</th>
                <th>Última Utilização</th>
                <th>Requisições</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id} className={!k.is_active ? 'row-inactive' : ''}>
                  <td>
                    <strong>{k.name}</strong>
                    {k.description && <small className="row-desc">{k.description}</small>}
                  </td>
                  <td><code className="key-preview">{k.key_preview}</code></td>
                  <td>
                    {/* Onda 107: escopo que não vale nesta vertical fica
                        MARCADO, não escondido — a chave existe e o escopo
                        está gravado nela; esconder faria parecer que não. */}
                    <div className="scopes-badges">
                      {(Array.isArray(k.scopes) ? k.scopes : []).map(s => {
                        const fora = !validos.includes(s);
                        return (
                          <span
                            key={s}
                            className={`scope-badge${fora ? ' scope-badge-fora' : ''}`}
                            title={fora ? `Não se aplica à vertical ${nomeDaVertical(vertical)}` : undefined}
                          >
                            {s}
                          </span>
                        );
                      })}
                    </div>
                    {vertical && (Array.isArray(k.scopes) ? k.scopes : []).some(s => !validos.includes(s)) && (
                      <small className="scope-fora-nota">
                        Escopos riscados não se aplicam à vertical {nomeDaVertical(vertical)}: as rotas que abrem
                        são de outra vertical.
                      </small>
                    )}
                  </td>
                  <td>{k.last_used_at ? new Date(k.last_used_at).toLocaleString('pt-BR') : '—'}</td>
                  <td className="text-center">{k.request_count?.toLocaleString('pt-BR') || 0}</td>
                  <td>
                    <span className={`status-badge ${k.is_active ? 'active' : 'revoked'}`}>
                      {k.is_active ? 'Ativa' : 'Revogada'}
                    </span>
                  </td>
                  <td>
                    <div className="action-btns">
                      {k.is_active
                        ? <button className="btn-danger-sm" onClick={() => handleRevoke(k.id)}>Revogar</button>
                        : <button className="btn-success-sm" onClick={() => handleActivate(k.id)}>Reativar</button>
                      }
                      <button className="btn-delete-sm" onClick={() => handleDelete(k.id)}>Excluir</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// A "Aba 2: Documentação Interativa" foi removida na Onda 65.
//
// Ela nasceu antes das Ondas 56 e 57 e descrevia só o CRUD daquela época: os
// exemplos não citavam `units/batch`, `sync-runs`, `visit-authorizations` nem
// o `Idempotency-Key` que as rotas novas EXIGEM. Não era apenas redundante em
// relação a /integration-docs — induzia ao erro quem a lesse.
//
// Documentação copiada envelhece sozinha. A que vale sai de uma fonte só
// (frontend/src/data/integrationDocs.js) e serve as duas verticais.

// A aba de log virou o componente compartilhado `components/ApiRequestLog`.
// Ele tambem serve a tela propria /api-request-log, que a Onda 66 abriu para o
// perfil de consulta: ler o historico de chamadas nao exige poder emitir chave.

// ─── Componente Principal ──────────────────────────────────────────────────
export default function ApiIntegration() {
  const [activeTab, setActiveTab] = useState('keys');
  const token = localStorage.getItem('token');
  // A vertical é da instalação e vem do login (Onda 43), já normalizada:
  // `condominio_residencial` e `condominio_corporativo` chegam como `condominio`.
  const { vertical } = useAuth();

  // Onda 67: os emojis saíram dos rótulos. O menu lateral e o resto da
  // plataforma identificam tela por ícone tipográfico; emoji numa aba tem
  // desenho e cor próprios, que mudam com o sistema operacional do usuário e
  // não obedecem à paleta.
  const tabs = [
    { id: 'keys', label: 'Chaves de API' },
    { id: 'logs', label: 'Log de Requisições' },
  ];

  return (
    <div className="api-integration">
      <div className="page-header">
        <h1>Chaves de API</h1>
        <p>
          Credenciais que o sistema de gestão usa para carregar o cadastro e autorizar visitas, com escopos
          granulares e histórico de uso. O contrato dos endpoints está em{' '}
          <Link to="/integration-docs">Documentação da Integração</Link>; o destino do webhook, em{' '}
          <Link to="/club-social">Integração e Outbox</Link>.
        </p>
      </div>

      <div className="tabs-bar">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`tab-btn ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'keys' && <KeysTab token={token} vertical={vertical} />}
      {activeTab === 'logs' && <ApiRequestLog token={token} />}
    </div>
  );
}
